import {
  TextureSource,
  Container,
  Graphics,
  RenderTexture,
  TilingSprite,
} from "pixi.js";
import {
  InstanceRendering,
  Isometry,
  MouseStartEvent,
  RayCastNode,
  RenderSystem,
} from "@/client/renderer/index";
import { camera_iso_to_scaled_viewport } from "@/client/camera";
import { match } from "ts-pattern";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { EditMode } from "@/editor/stores/editor";
import { LayerKind } from "@/editor/blueprints/LayerKind";
import { NodeContainer } from "@/editor/stores/game-instances";
import {
  get_simulation_scale,
  get_stage_height,
  get_stage_width,
} from "@/client/config/config";

export function adjust_selected_tile_size(
  renderer: InstanceRendering,
  brush: number[][],
) {
  if (renderer.grid && brush.length > 0 && brush[0].length > 0) {
    const height = brush.length;
    const width = brush[0].length;
    renderer.grid.grid_container.removeChild(renderer.grid.selected_tile);
    renderer.grid.selected_tile = new Graphics()
      .rect(
        0,
        0,
        renderer.terrain_params.tile_width * width,
        renderer.terrain_params.tile_height * height,
      )
      .fill({
        color: "#9999ff",
        alpha: 0.5,
      });
    renderer.grid.grid_container.addChild(renderer.grid.selected_tile);
  }
}

export function init_grid(
  renderer_system: RenderSystem,
  renderer: InstanceRendering,
  edit_mode: EditMode,
) {
  if (!renderer.grid) {
    const textureSource = new TextureSource({
      width: renderer.terrain_params.tile_width,
      height: renderer.terrain_params.tile_height,
    });
    const renderTexture = new RenderTexture({ source: textureSource });
    const graphics = new Graphics()
      .rect(0, 0, 1, renderer.terrain_params.tile_height)
      .rect(0, 0, renderer.terrain_params.tile_width, 1)
      .fill({
        color: "#ffffff",
        alpha: 0.5,
      });
    renderer_system.renderer.render({
      target: renderTexture,
      container: graphics,
    });
    const selected_tile = new Graphics()
      .rect(
        0,
        0,
        renderer.terrain_params.tile_width,
        renderer.terrain_params.tile_height,
      )
      .fill({
        color: "#9999ff",
        alpha: 0.5,
      });
    const grid: InstanceRendering["grid"] = {
      sprite: new TilingSprite({
        texture: renderTexture,
        height: 1440,
        width: 2560,
        alpha: 0,
      }),
      mouse_wheel_event: new SimpleEventDispatcher<number>(),
      grid_container: new Container(),
      p_scaling: { x: 1, y: 1 },
      current_ray_cast_nodes: [],
      last_mouse_move_position: { x: 0, y: 0 },
      selected_tile,
    };
    let left_click_start: MouseStartEvent | null = null;
    let middle_click_start: MouseStartEvent | null = null;
    let left_click_down = false;
    grid.grid_container.addChild(grid.sprite);
    grid.grid_container.interactive = true;

    grid.grid_container.on("pointerdown", (mouse_event) => {
      match(mouse_event.button)
        .with(0, () => {
          const [x, y] = [
            -(grid.sprite.tilePosition.x - grid.selected_tile.x) /
              renderer.terrain_params.tile_width,
            -(grid.sprite.tilePosition.y - grid.selected_tile.y) /
              renderer.terrain_params.tile_height,
          ];
          left_click_down = true;
          window.medium_gui.editor.select_tile_position({
            x: x,
            y: y - 1,
          });
          left_click_start = {
            click_start: { x: mouse_event.x, y: mouse_event.y },
            camera_iso_start: { ...renderer.camera.camera_isometry },
          };
          const scale = get_simulation_scale();
          window.medium_gui.editor.grid_left_click_start({
            mouseX: mouse_event.x,
            mouseY: mouse_event.y,
            tileX: x,
            tileY: y - 1,
            altKey: mouse_event.altKey,
            ctrlKey: mouse_event.ctrlKey,
            shiftKey: mouse_event.shiftKey,
            zoom: renderer.camera.zoom,
            start: left_click_start,
            simulation_coordinates: {
              x:
                ((mouse_event.globalX +
                  renderer.camera.camera_isometry.x / renderer.camera.zoom) *
                  renderer.camera.zoom -
                  (get_stage_width() * renderer.camera.zoom) / 2) /
                scale,
              y:
                ((mouse_event.globalY +
                  renderer.camera.camera_isometry.y / renderer.camera.zoom) *
                  renderer.camera.zoom -
                  (get_stage_height() * renderer.camera.zoom) / 2) /
                scale,
            },
            camera_iso: { ...renderer.camera.camera_isometry },
            nodes: grid.current_ray_cast_nodes,
          });
        })
        .with(1, () => {
          middle_click_start = {
            click_start: { x: mouse_event.x, y: mouse_event.y },
            camera_iso_start: { ...renderer.camera.camera_isometry },
          };
        })
        .otherwise(() => {});
    });

    grid.grid_container.on("wheel", (wheel) => {
      if (wheel.altKey) {
        grid.mouse_wheel_event.dispatch(wheel.deltaY);
      }
    });

    grid.grid_container.on("pointerup", (mouse_event) => {
      match(mouse_event.button)
        .with(0, () => {
          left_click_down = false;
          const [x, y] = [
            -(grid.sprite.tilePosition.x - grid.selected_tile.x) /
              renderer.terrain_params.tile_width,
            -(grid.sprite.tilePosition.y - grid.selected_tile.y) /
              renderer.terrain_params.tile_height,
          ];
          const scale = get_simulation_scale();
          window.medium_gui.editor.grid_left_click_end({
            mouseX: mouse_event.x,
            mouseY: mouse_event.y,
            tileX: x,
            tileY: y - 1,
            altKey: mouse_event.altKey,
            ctrlKey: mouse_event.ctrlKey,
            shiftKey: mouse_event.shiftKey,
            zoom: renderer.camera.zoom,
            simulation_coordinates: {
              x:
                ((mouse_event.globalX +
                  renderer.camera.camera_isometry.x / renderer.camera.zoom) *
                  renderer.camera.zoom -
                  (get_stage_width() * renderer.camera.zoom) / 2) /
                scale,
              y:
                ((mouse_event.globalY +
                  renderer.camera.camera_isometry.y / renderer.camera.zoom) *
                  renderer.camera.zoom -
                  (get_stage_height() * renderer.camera.zoom) / 2) /
                scale,
            },
            camera_iso: { ...renderer.camera.camera_isometry },
            start: left_click_start,
            nodes: grid.current_ray_cast_nodes,
          });
          left_click_start = null;
        })
        .with(1, () => {
          middle_click_start = null;
        })
        .otherwise(() => {});
    });

    grid.grid_container.addChild(selected_tile);
    renderer_system.global_mouse_position.sub((mouse_event) => {
      if (middle_click_start) {
        const diff = {
          x: mouse_event.x - middle_click_start.click_start.x,
          y: mouse_event.y - middle_click_start.click_start.y,
        };
        renderer.camera.update_camera_position({
          x:
            middle_click_start.camera_iso_start.x -
            diff.x * renderer.camera.zoom,
          y:
            middle_click_start.camera_iso_start.y -
            diff.y * renderer.camera.zoom,
          rotation: middle_click_start.camera_iso_start.rotation,
        });
      }
      grid.last_mouse_move_position = mouse_event;
      const new_selected_tile_position =
        get_current_selected_tile_position(renderer);

      if (new_selected_tile_position !== null) {
        const [new_selected_tile_x, new_selected_tile_y] =
          new_selected_tile_position;
        if (
          new_selected_tile_x != grid.selected_tile.x ||
          new_selected_tile_y != grid.selected_tile.y
        ) {
          if (left_click_down) {
            const [x, y] = [
              -(grid.sprite.tilePosition.x - new_selected_tile_x) /
                renderer.terrain_params.tile_width,
              -(grid.sprite.tilePosition.y - new_selected_tile_y) /
                renderer.terrain_params.tile_height,
            ];
            window.medium_gui.editor.select_tile_position({
              x,
              y: y - 1,
            });
          }
          grid.selected_tile.x = new_selected_tile_x;
          grid.selected_tile.y = new_selected_tile_y;
        }
      }
      if (
        window.medium_gui.editor.edit_mode == "select" ||
        window.medium_gui.editor.edit_mode == "remove"
      ) {
        grid.current_ray_cast_nodes = get_nodes_from_ray_cast(
          renderer,
          mouse_event.x,
          mouse_event.y,
        );
        const [x, y] = [
          -(grid.sprite.tilePosition.x - grid.selected_tile.x) /
            renderer.terrain_params.tile_width,
          -(grid.sprite.tilePosition.y - grid.selected_tile.y) /
            renderer.terrain_params.tile_height,
        ];
        const scale = get_simulation_scale();
        window.medium_gui.editor.grid_mouse_move({
          mouseX: mouse_event.x,
          mouseY: mouse_event.y,
          tileX: x,
          tileY: y - 1,
          altKey: mouse_event.altKey,
          ctrlKey: mouse_event.ctrlKey,
          shiftKey: mouse_event.shiftKey,
          zoom: renderer.camera.zoom,
          simulation_coordinates: {
            x:
              ((mouse_event.globalX +
                renderer.camera.camera_isometry.x / renderer.camera.zoom) *
                renderer.camera.zoom -
                (get_stage_width() * renderer.camera.zoom) / 2) /
              scale,
            y:
              ((mouse_event.globalY +
                renderer.camera.camera_isometry.y / renderer.camera.zoom) *
                renderer.camera.zoom -
                (get_stage_height() * renderer.camera.zoom) / 2) /
              scale,
          },
          camera_iso: { ...renderer.camera.camera_isometry },
          start: left_click_start,
          nodes: grid.current_ray_cast_nodes,
        });
      }
    });
    renderer.grid = grid;
    selected_tile.alpha = edit_mode === "paint" ? 1 : 0;
    renderer.main_container.addChild(renderer.grid.grid_container);
  }
}
function get_nodes_from_ray_cast(
  renderer: InstanceRendering,
  ray_position_x: number,
  ray_position_y: number,
): RayCastNode[] {
  const nodes: RayCastNode[] = [];
  iterate_all_layers(renderer, (display_object) => {
    const { minX, maxX, minY, maxY } = display_object.getBounds();
    if (
      ray_position_x > minX &&
      ray_position_x < maxX &&
      ray_position_y > minY &&
      ray_position_y < maxY
    ) {
      const game_node_container = display_object as NodeContainer;
      if (game_node_container.game_node_id) {
        nodes.push({
          game_node_id: game_node_container.game_node_id,
          entity_id: game_node_container.entity_id,
          instance_node_id: game_node_container.instance_node_id,
        });
      }
    }
  });
  return nodes;
}

function iterate_all_layers(
  renderer: InstanceRendering,
  callback: (display_object: Container) => void,
) {
  const layer_map = renderer.layer_map;
  for (const layer in layer_map) {
    for (const display_object of layer_map[layer as LayerKind].children) {
      iterate_display_object(display_object, callback);
    }
  }
}

function iterate_display_object(
  display_object: Container,
  callback: (display_object: Container) => void,
) {
  callback(display_object);
  for (const child of display_object.children) {
    iterate_display_object(child, callback);
  }
}

export function show_grid(renderer: InstanceRendering) {
  if (renderer.grid) {
    renderer.grid.selected_tile.alpha = 1;
  }
}

export function hide_grid(renderer: InstanceRendering) {
  if (renderer.grid) {
    renderer.grid.selected_tile.alpha = 0;
  }
}

export function toggle_grid_lines(renderer: InstanceRendering) {
  if (renderer.grid) {
    renderer.grid.sprite.alpha = renderer.grid.sprite.alpha === 0 ? 1 : 0;
  }
}

export function get_current_selected_tile_position(
  renderer: InstanceRendering,
): [number, number] | null {
  const grid = renderer.grid;
  if (!grid) {
    return null;
  }
  const localPosition = grid.grid_container.toLocal(grid.sprite.tilePosition);

  const [local_x, local_y] = [
    Math.round(localPosition.x * (1 / renderer.camera.zoom)),
    Math.round(localPosition.y * (1 / renderer.camera.zoom)),
  ];
  const diff = {
    x: local_x - grid.last_mouse_move_position.x * renderer.camera.zoom,
    y: local_y - grid.last_mouse_move_position.y * renderer.camera.zoom,
  };
  const selected_new_x =
    local_x -
    Math.floor(
      (diff.x + renderer.terrain_params.tile_width) /
        renderer.terrain_params.tile_width,
    ) *
      renderer.terrain_params.tile_width;
  const selected_new_y =
    local_y -
    Math.floor(
      (diff.y + renderer.terrain_params.tile_height) /
        renderer.terrain_params.tile_height,
    ) *
      renderer.terrain_params.tile_height;
  return [selected_new_x, selected_new_y];
}

export function update_grid(
  camera_isometry: Isometry,
  renderer: InstanceRendering,
) {
  if (renderer.grid) {
    const new_iso = camera_iso_to_scaled_viewport(
      camera_isometry,
      renderer.camera.zoom,
      {
        y_pscaling: renderer.grid.p_scaling.y,
        x_pscaling: renderer.grid.p_scaling.x,
      },
    );

    if (
      renderer.grid.sprite.tilePosition.x !== new_iso.x ||
      renderer.grid.sprite.tilePosition.y !== new_iso.y
    ) {
      renderer.grid.sprite.tilePosition.x = new_iso.x;
      renderer.grid.sprite.tilePosition.y = new_iso.y;
    }
  }
}
